/**
 * ForgotPasswordPage Component
 * VROONG 비밀번호 찾기 페이지
 *
 * @description
 * 이메일을 통한 비밀번호 재설정 링크 전송
 * 아이디와 이메일 입력 후 재설정 링크 발송
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastPosition, ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { extractErrorMessage } from '@/shared/utils/errorHandler';
import { UserIcon, EmailIcon, LoginIcon } from '@/shared/components/icons/Icons';
import FormInput from '@/shared/components/forms/FormInput';
import PrimaryButton from '@/shared/components/buttons/PrimaryButton';
import LinkButton from '@/shared/components/buttons/LinkButton';
import {
    validateUserId,
    validateEmail,
    ValidationErrors,
} from '@/features/auth/lib/validation';
import { LOGIN_FEATURES } from '@/features/auth/config/feature.config';
import { authApi } from '../../../api/authApi';
import BrandSection from '../../ui/BrandSection/BrandSection';
import VersionInfo from '../../ui/VersionInfo';
import '@/features/auth/styles/auth.css';

// ============================================================
// 상수 정의
// ============================================================

/** 토스트 기본 옵션 */
const TOAST_OPTIONS = {
    position: ToastPosition.TOP_CENTER,
} as const;

/** 성공 아이콘 컨테이너 스타일 */
const SUCCESS_ICON_STYLE: React.CSSProperties = {
    width: '80px',
    height: '80px',
    margin: '0 auto 1.5rem',
    background: 'linear-gradient(135deg, #05CD82 0%, #0091FF 100%)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

/** 성공 메시지 텍스트 스타일 */
const SUCCESS_MESSAGE_STYLE: React.CSSProperties = {
    marginTop: '1rem',
    lineHeight: '1.6',
};

/** 안내사항 컨테이너 스타일 */
const INFO_BOX_STYLE: React.CSSProperties = {
    marginTop: '2rem',
    padding: '1rem',
    background: '#F0FDF9',
    borderRadius: '8px',
    fontSize: '0.875rem',
    color: '#059669',
    lineHeight: '1.6',
};

/** 안내사항 리스트 스타일 */
const INFO_LIST_STYLE: React.CSSProperties = {
    marginTop: '0.5rem',
    marginLeft: '1.25rem',
};

/** 로그인 버튼 스타일 */
const LOGIN_BUTTON_STYLE: React.CSSProperties = {
    marginTop: '1.5rem',
};

// ============================================================
// 컴포넌트 구현
// ============================================================

/**
 * 비밀번호 찾기 페이지
 */
const ForgotPasswordPage: React.FC = () => {
    // ======== Hooks ========
    const { showToast } = useToast();
    const navigate = useNavigate();

    // ======== State ========
    const [step, setStep] = useState<'input' | 'sent'>('input');
    const [userId, setUserId] = useState('');
    const [emailInfo, setEmailInfo] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<ValidationErrors>({});

    // ======== Memoized Values ========
    /** 브랜드 섹션 설명 텍스트 */
    const brandDescription = useMemo(() => (
        <>
            효율적이고 투명한 라이더 정산 관리
            <br />
            VROONG과 함께 더 나은 배달 서비스를 만들어가세요
        </>
    ), []);

    // ======== Validation ========
    /**
     * 폼 유효성 검증
     */
    const validateForm = useCallback((): boolean => {
        const errors: ValidationErrors = {};

        const userIdError = validateUserId(userId, 2);
        if (userIdError) errors.userId = userIdError;

        const emailError = validateEmail(emailInfo, true);
        if (emailError) errors.emailInfo = emailError;

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    }, [userId, emailInfo]);

    // ======== Event Handlers ========
    /**
     * 아이디 입력 핸들러
     */
    const handleUserIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setUserId(e.target.value);
    }, []);

    /**
     * 이메일 입력 핸들러
     */
    const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEmailInfo(e.target.value);
    }, []);

    /**
     * 비밀번호 재설정 이메일 전송 처리
     */
    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await authApi.forgotPassword({ userId, emailInfo });

            showToast(response.message, {
                type: ToastType.SUCCESS,
                ...TOAST_OPTIONS,
            });

            setStep('sent');
        } catch (err: unknown) {
            const errorMessage = extractErrorMessage(err, '이메일 전송에 실패했습니다.');
            showToast(errorMessage, { type: ToastType.ERROR, ...TOAST_OPTIONS });
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [validateForm, userId, emailInfo, showToast]);

    /**
     * 로그인 페이지 이동
     */
    const handleNavigateToLogin = useCallback(() => {
        navigate('/login');
    }, [navigate]);

    /**
     * 회원가입 페이지 이동
     */
    const handleNavigateToRegister = useCallback(() => {
        navigate('/register');
    }, [navigate]);

    // ======== Render ========
    return (
        <div className="login-page">
            <div className="login-container">
                {/* 좌측 브랜드 섹션 */}
                <BrandSection
                    title="라이더 정산 시스템"
                    description={brandDescription}
                    features={LOGIN_FEATURES}
                />

                {/* 우측 비밀번호 찾기 섹션 */}
                <div className="login-section">
                    <div className="login-form-container">
                        {step === 'input' ? (
                            <>
                                {/* 입력 단계 헤더 */}
                                <div className="login-header">
                                    <h2>비밀번호 찾기</h2>
                                    <p>가입 시 등록한 아이디와 이메일을 입력하세요</p>
                                </div>

                                {/* 비밀번호 찾기 폼 */}
                                <form className="login-form" onSubmit={handleSubmit}>
                                    {/* 아이디 */}
                                    <FormInput
                                        id="userId"
                                        name="userId"
                                        value={userId}
                                        onChange={handleUserIdChange}
                                        placeholder="아이디를 입력하세요"
                                        label="아이디"
                                        required
                                        disabled={isLoading}
                                        icon={<UserIcon />}
                                        error={fieldErrors.userId}
                                    />

                                    {/* 이메일 */}
                                    <FormInput
                                        id="emailInfo"
                                        name="emailInfo"
                                        type="email"
                                        value={emailInfo}
                                        onChange={handleEmailChange}
                                        placeholder="example@vroong.com"
                                        label="이메일"
                                        required
                                        disabled={isLoading}
                                        icon={<EmailIcon />}
                                        error={fieldErrors.emailInfo}
                                    />

                                    {/* 에러 메시지 */}
                                    {error && <div className="error-message">{error}</div>}

                                    <PrimaryButton
                                        type="submit"
                                        loading={isLoading}
                                        disabled={isLoading}
                                    >
                                        재설정 링크 전송
                                    </PrimaryButton>
                                </form>
                            </>
                        ) : (
                            <>
                                {/* 전송 완료 단계 헤더 */}
                                <div className="login-header">
                                    <div style={SUCCESS_ICON_STYLE}>
                                        <svg
                                            width="40"
                                            height="40"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="white"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                            />
                                        </svg>
                                    </div>
                                    <h2>이메일이 전송되었습니다</h2>
                                    <p style={SUCCESS_MESSAGE_STYLE}>
                                        <strong>{emailInfo}</strong>으로
                                        <br />
                                        비밀번호 재설정 링크를 전송했습니다.
                                        <br />
                                        이메일을 확인해주세요.
                                    </p>
                                </div>

                                {/* 안내사항 */}
                                <div style={INFO_BOX_STYLE}>
                                    <strong>안내사항</strong>
                                    <ul style={INFO_LIST_STYLE}>
                                        <li>이메일이 오지 않으면 스팸 메일함을 확인해주세요</li>
                                        <li>재설정 링크는 24시간 동안 유효합니다</li>
                                        <li>링크를 클릭하여 새 비밀번호를 설정하세요</li>
                                    </ul>
                                </div>

                                <PrimaryButton
                                    onClick={handleNavigateToLogin}
                                    style={LOGIN_BUTTON_STYLE}
                                >
                                    로그인 페이지로 이동
                                </PrimaryButton>
                            </>
                        )}

                        {/* 추가 옵션 */}
                        <div className="additional-options">
                            <div className="divider">
                                <span>또는</span>
                            </div>

                            <div className="help-links">
                                <LinkButton onClick={handleNavigateToLogin}>
                                    <LoginIcon width={16} height={16} />
                                    로그인 페이지로
                                </LinkButton>
                                <LinkButton onClick={handleNavigateToRegister}>
                                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                    </svg>
                                    계정 신청
                                </LinkButton>
                            </div>
                        </div>
                    </div>

                    {/* 하단 버전 정보 */}
                    <VersionInfo />
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
